import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMe } from '@/session/meHooks';
import { VirtualAccountsListContext } from '@/features/virtualAccounts/virtualAccountsListContext';
import {
  fetchOwnedEnvelopes,
  ownedVirtualAccountsFrom,
  OwnedEnvelopesContext,
  type OwnedEnvelope,
  type OwnedEnvelopesStatus,
  type OwnedEnvelopesValue,
} from './useOwnedEnvelopes';

interface OwnedEnvelopesProviderProps {
  children: ReactNode;
}

/**
 * One owned-envelope fetch per account-page mount (TMMAPP-0008). `reload` after an envelope is created
 * elsewhere is not needed here. The destination groups' balances come from the shell's shared
 * virtual-accounts list when it is mounted (TMMAPP-0109); outside the shell they are null.
 */
export function OwnedEnvelopesProvider({ children }: OwnedEnvelopesProviderProps) {
  const virtualAccounts = useMe().virtualAccounts;
  const served = useContext(VirtualAccountsListContext)?.items;
  const [status, setStatus] = useState<OwnedEnvelopesStatus>('loading');
  const [envelopes, setEnvelopes] = useState<OwnedEnvelope[]>([]);
  const [error, setError] = useState<unknown>(undefined);
  const started = useRef(false);

  const reload = useCallback(async () => {
    try {
      setEnvelopes(await fetchOwnedEnvelopes(virtualAccounts));
      setStatus('ready');
      setError(undefined);
    } catch (failure) {
      setStatus((prev) => (prev === 'ready' ? prev : 'error'));
      setError(failure);
    }
  }, [virtualAccounts]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void reload();
  }, [reload]);

  const owned = useMemo(
    () => ownedVirtualAccountsFrom(virtualAccounts, served ?? []),
    [virtualAccounts, served],
  );
  const value = useMemo<OwnedEnvelopesValue>(
    () => ({ status, error, envelopes, virtualAccounts: owned, reload }),
    [status, error, envelopes, owned, reload],
  );
  return <OwnedEnvelopesContext.Provider value={value}>{children}</OwnedEnvelopesContext.Provider>;
}
